<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-start mb-1 flex-wrap"
    >
      <!-- 🔍 Search Input on the Left -->
      <b-button v-b-toggle.collapse-1 variant="outline-primary">
        {{ t('Filters') }}
      </b-button>

      <!-- ➕ Buttons on the Right -->
      <div class="d-flex ml-auto">
        <b-button
          v-if="checkPermission(constants.ContactUniversePermssions.Create)"
          variant="primary"
          @click="redirectToCreate"
        >
          <feather-icon icon="PlusIcon" />
          {{ t('Add Contact') }}
        </b-button>

        <b-button
          v-if="checkPermission(constants.ContactUniversePermssions.Import)"
          variant="primary"
          class="ml-1"
          @click="showModal = true"
        >
          <feather-icon icon="PlusIcon" />
          {{ t('Import Contacts') }}
        </b-button>
      </div>
    </div>

    <b-collapse id="collapse-1" class="mb-2">
      <b-row class="mb-2">
        <b-col cols="6" md="3">
          <b-form-group class="mb-0">
            <b-form-input
              id="vi-search"
              v-model="form.search"
              type="text"
              autocomplete="off"
              :placeholder="t('Search')"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" class="text-center mt-1">
          <b-button variant="success mx-1" type="button" @click="getContacts">
            {{ t('Search') }}
          </b-button>
          <b-button variant="danger mx-1" @click="resetFilter">
            {{ t('Reset') }}
          </b-button>
        </b-col>
      </b-row>
    </b-collapse>
    <b-card>
      <b-table
        sticky-header
        responsive="true"
        :items="contacts.data"
        :fields="contactFields"
        @row-contextmenu="contextmenu"
        @sort-changed="sortChanged"
      >
        <template #cell(full_name)="data">
          <span class="">{{ data.item.full_name || 'N/A' }}</span>
        </template>
        <template #cell(phone)="data">
          <span class="">{{ data.item.phone || 'N/A' }}</span>
        </template>
        <template #cell(title)="data">
          <span class="">{{ data.item.title || 'N/A' }}</span>
        </template>
        <template #cell(company)="data">
          <span class="">{{ data.item.company || 'N/A' }}</span>
        </template>
        <template #cell(email)="data">
          <span class="">{{ data.item.email || 'N/A' }}</span>
        </template>
        <template #cell(industry)="data">
          <span class="">{{ data.item.industry || 'N/A' }}</span>
        </template>
        <template #cell(created_at)="data">
          <span class="">{{
            data.item.created_at ? dFormat(data.item.created_at) : 'N/A'
          }}</span>
        </template>
        <template #cell(created_by_name)="data">
          <span class="">{{ data.item.created_by_name || 'N/A' }}</span>
        </template>

        <template v-if="contacts.total == 0" #table-caption>
          <div class="text-center">
            {{ t('No Record Found') }}
          </div>
        </template>
      </b-table>
      <b-row v-if="contacts.total">
        <b-col cols="4" class="d-flex">
          <div class="align-self-center pr-2">
            {{ t('Total Entries') + ' ' + contacts.total }}
          </div>
          <b-form-group class="align-self-center mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{
              t('Per page')
            }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="form.size"
              size="sm"
              :options="tableFilter.pageOptions"
              class="w-70"
              @change="getContacts"
            />
          </b-form-group>
        </b-col>
        <b-col cols="8">
          <pagination
            :data="contacts"
            :limit="2"
            class="pt-1 justify-content-end"
            @pagination-change-page="getResults"
          />
        </b-col>
      </b-row>
    </b-card>
    <vue-context ref="contactsContextMenu">
      <li v-for="data in menuData" :key="data.text">
        <b-link
          v-if="data.permission && checkPermission(data.permission)"
          class="d-flex align-items-center"
          @click="optionClicked(data.id)"
        >
          <feather-icon :icon="data.icon" size="16" />
          <span class="ml-75">{{ data.text }}</span>
        </b-link>
      </li>
    </vue-context>

    <b-modal
      id="uploadCSVModal"
      v-model="showModal"
      :title="t('Upload CSV')"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      hide-footer
      @hidden="uploadModalCancel"
    >
      <validation-observer ref="uploadCSVModal">
        <b-form
          enctype="multipart/form-data"
          autocomplete="off"
          @submit.prevent="handleUpload"
        >
          <b-form-group label-for="vi-csv-file" class="required">
            <validation-provider
              #default="{ errors: veeErrors }"
              class="input-group"
              name="CSV File"
              vid="vi-csv-file"
              rules="required"
            >
              <b-input-group class="input-group-merge">
                <b-form-file
                  v-model="importFileForm.file"
                  accept=".csv,text/csv"
                  :class="
                    veeErrors[0] || errors.file
                      ? 'form-control border-danger'
                      : ''
                  "
                  :placeholder="t('Upload CSV')"
                  :browse-text="t('Browse')"
                  @change="validateCsvFile"
                />
              </b-input-group>
              <small v-if="veeErrors.length" class="text-danger">{{
                t(veeErrors[0])
              }}</small>
              <div v-if="parsedFileErrors.length" class="text-danger">
                <div
                  v-for="(errorRow, i) in parsedFileErrors"
                  :key="i"
                  class="mb-2"
                >
                  <template v-if="errorRow.row">
                    <strong v-if="errorRow.row">Row {{ errorRow.row }}</strong>
                    <ul class="mb-0 pl-3">
                      <li v-for="(msg, j) in errorRow.messages" :key="j">
                        {{ msg }}
                      </li>
                    </ul>
                  </template>
                  <template v-else>
                    <span
                      v-for="(msg, j) in errorRow.messages"
                      :key="j"
                      class="mb-0 mr-0 pl-0"
                      >{{ msg }}</span
                    >
                  </template>
                </div>
              </div>
            </validation-provider>
          </b-form-group>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <b-link @click="downloadSampleCsv">
                {{ t('Donwload Sample CSV') }}
              </b-link>
            </div>
            <div>
              <b-button type="submit" variant="primary" class="mr-1">
                {{ t('Submit') }}
              </b-button>
              <b-button
                type="button"
                variant="outline-secondary"
                @click="uploadModalCancel"
              >
                {{ t('Cancel') }}
              </b-button>
            </div>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
    <CreateContactActivity />
  </div>
</template>
<script>
import 'bootstrap-vue'
import VueContext from 'vue-context'
import { useUtils } from '@core/libs/i18n'
import { required } from '@validations'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import store from '@/store'
import constants from '@/constants'
import { tableFilter, showLoader, hideLoader, dFormat } from '@/utils/common'
import checkPermissions from '@/utils/checkPermissons'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import CreateContactActivity from './CreateContactActivity.vue'

export default {
  components: {
    VueContext,
    ValidationProvider,
    ValidationObserver,
    CreateContactActivity,
  },
  setup() {
    const { t } = useUtils()
    return { t }
  },
  data() {
    return {
      required,
      constants,
      contacts: {},
      valuesContext: {},
      tableFilter,
      form: {
        search: '',
        sortBy: 'id',
        sortOrder: 'desc',
        size: 10,
        page: 1,
      },
      importFileForm: {
        file: null,
        fileName: '',
      },
      searchDebounceTimer: null,
      showModal: false,
      errors: {
        file: null,
      },
    }
  },
  computed: {
    contactFields() {
      return [
        { key: 'full_name', label: this.$i18n.t('Full Name'), sortable: true },
        { key: 'email', label: this.$i18n.t('Email'), sortable: true },
        { key: 'phone', label: this.$i18n.t('Phone'), sortable: true },
        { key: 'title', label: this.$i18n.t('Title'), sortable: true },
        { key: 'company', label: this.$i18n.t('Company'), sortable: true },
        { key: 'industry', label: this.$i18n.t('Industry'), sortable: true },
        {
          key: 'created_at',
          label: this.$i18n.t('Created Date'),
          sortable: true,
        },
        {
          key: 'created_by_name',
          label: this.$i18n.t('Created By'),
          sortable: true,
        },
      ]
    },
    menuData() {
      return [
        {
          id: 1,
          icon: 'EditIcon',
          text: this.$i18n.t('Edit'),
          permission: constants.ContactUniversePermssions.Update,
        },
        {
          id: 2,
          icon: 'TrashIcon',
          text: this.$i18n.t('Delete'),
          permission: constants.ContactUniversePermssions.Delete,
        },
        {
          id: 3,
          icon: 'PlusIcon',
          text: this.$i18n.t('Add Activity'),
          permission: constants.ContactUniversePermssions.CreateActivity,
        },
        {
          id: 4,
          icon: 'EyeIcon',
          text: this.$i18n.t('View Activities'),
          permission: constants.ContactUniversePermssions.ViewActivity,
        },
      ]
    },
    parsedFileErrors() {
      const fileErrors = this.errors?.file || []
      return fileErrors.map(err => {
        const [rowPart] = err.split(':')
        const rowMatch = rowPart.match(/Row (\d+)/)
        const row = rowMatch ? parseInt(rowMatch[1], 10) : null

        const errorText = err.replace(/Row \d+:\s*/, '')
        const fieldMessages = errorText.split(',').map(f => f.trim())

        return {
          row,
          messages: fieldMessages,
        }
      })
    },
  },
  mounted() {
    this.getContacts()
  },
  methods: {
    downloadSampleCsv() {
      const link = document.createElement('a')
      link.href = '/samplecsv/contact_universe.csv'
      link.setAttribute('download', 'contact_universe.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    uploadModalCancel() {
      this.showModal = false
      this.importFileForm.file = null
      this.errors = {}
    },
    validateCsvFile(event) {
      this.errors.file = null
      const file = event.target.files[0]
      const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv')
      this.csvError = !isCsv
      if (this.csvError) {
        this.importFileForm.file = null
        this.errors.file = [this.$t('Please select a valid CSV file.')]
      }
    },
    async handleUpload() {
      this.$refs.uploadCSVModal.validate().then(async success => {
        if (success) {
          const formData = new FormData()
          formData.append('csv_file', this.importFileForm.file)
          showLoader()
          await store
            .dispatch('ManageContact/importContact', formData)
            .then(response => {
              if (response.data.code === constants.SUCCESS) {
                if (response.data.data.failed_count === 0) {
                  this.showModal = false
                  this.importFileForm.file = null
                  this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                      title: `${response.data.msg}`,
                      icon: 'Success',
                      variant: 'success',
                      text: '',
                    },
                  })
                  this.getContacts()
                } else {
                  this.errors.file = this.getFormattedErrors(
                    response.data.data.errors
                  )
                }
                hideLoader()
              } else if (response.data.code === '422') {
                this.errors.file = response.data.errors?.csv_file
                hideLoader()
              }
            })
          hideLoader()
        }
      })
    },
    getFormattedErrors(errorArray) {
      return errorArray.map(e => {
        const messages = Object.entries(e.errors)
          .flatMap(([field, msgs]) => msgs.map(msg => `${field}: ${msg}`))
          .join(', ')
        return `Row ${e.row}: ${messages}`
      })
    },
    resetFilter() {
      this.form = {
        search: '',
        sortBy: 'id',
        sortOrder: 'desc',
        size: 10,
        page: 1,
      }
      this.getContacts()
    },
    redirectToCreate() {
      this.$router.push('/contact-universe/contacts/create')
    },
    dFormat(date, format = 'DD MMMM YYYY hh:mm A') {
      return dFormat(date, format)
    },
    sortChanged(val) {
      this.form.sortBy = val.sortBy
      this.form.sortOrder = val.sortDesc ? 'desc' : 'asc'
      this.getContacts()
    },
    checkPermission(val) {
      return checkPermissions(val)
    },
    recieveData(val) {
      this.contacts = val
    },
    async getResults(page = 1) {
      this.form.page = page
      this.getContacts()
    },
    async getContacts() {
      showLoader()
      await store
        .dispatch('ManageContact/getContacts', this.form)
        .then(response => {
          if (response.data.code === constants.SUCCESS) {
            this.contacts = response.data.data
            document.getElementById('loading-bg').classList.remove('loading-bg')
            // eslint-disable-next-line eqeqeq
          }
        })
      hideLoader()
    },
    async deleteContacts(id) {
      this.$swal({
        title: 'Are you sure ?',
        text: 'You want to Delete this Contact!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store
            .dispatch('ManageContact/deleteContact', { id })
            .then(response => {
              if (response.data.code === constants.SUCCESS) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: `${response.data.msg}`,
                    icon: 'Success',
                    variant: 'success',
                    text: '',
                  },
                })
                this.getContacts()
              }
            })
        }
      })
    },
    contextmenu(item, index, event) {
      event.preventDefault()
      this.valuesContext = item
      this.$refs.contactsContextMenu.open(event, item)
    },
    optionClicked(id) {
      if (id === 1) {
        this.$root.$router.push(
          `/contact-universe/contacts/edit/${this.valuesContext.id}`
        )
      } else if (id === 2) {
        this.deleteContacts(this.valuesContext.id)
      } else if (id === 3) {
        this.addActivity(this.valuesContext.id)
      } else if (id === 4) {
        this.$root.$router.push(
          `/contact-universe/contacts/${this.valuesContext.id}/activity`
        )
      }
    },
    async addActivity(contactId) {
      showLoader()
      this.$bvModal.show('createContactActivtyModal')
      this.$root.$emit('getContactId', contactId)
      hideLoader()
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-context.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
